import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { RollupLocation, RollupResponse, RollupRow } from '../api/types';
import { useApp } from '../state/AppContext';
import { useWeekAndRegion } from '../state/useFilters';
import { countStatus } from '../state/periods';
import { EmptyRow, ErrorBanner, SortableHeader, downloadCsv, formatDate, formatLongDate, useTableSort } from '../components/ui';
import { RegionSelect, WeekSelect } from '../components/Filters';
import { TonePill } from '../components/Tone';

/** How a supply's status sorts: short first, then no target yet, then OK. */
const STATUS_RANK: Record<RollupRow['status'], number> = { SHORT: 0, 'SET TARGET': 1, OK: 2 };

/** On hand as a share of the minimum, for sorting "against minimum"; blank where there is no minimum. */
const shareOfMinimum = (onHand: number | undefined, minimum: number | undefined) =>
  (minimum != null ? (onHand ?? 0) / Math.max(minimum, 1) : null);

/**
 * The week's counted inventory by supply and group — the spreadsheet's
 * dashboard, kept — with what the spreadsheet could not say: how many locations
 * the totals actually cover, and which locations are below their own minimum.
 */
export function ThisWeek() {
  const { reference, locationsFor, regionName, typeOf } = useApp();
  const { week, region, setWeek, setRegion } = useWeekAndRegion();
  const [view, setView] = useState<'supply' | 'location'>('supply');
  const [pickedSupply, setPickedSupply] = useState<number | null>(null);
  const [pickedLocation, setPickedLocation] = useState<number | null>(null);

  const rollup = useQuery({
    queryKey: ['rollup', week, region],
    queryFn: () => api.get<RollupResponse>('/api/rollup', { weekEnding: week, region }),
  });
  const data = rollup.data;

  const groupShort = (groupId: number) => data?.groups.find((g) => g.groupId === groupId)?.shortName ?? '';
  const missing = data ? data.coverage.active - data.coverage.reported : 0;
  const isCurrent = week === reference.currentWeekEnding;

  const exportTotals = () => {
    if (!data) return;
    downloadCsv(
      `inventory-totals-week-ending-${data.weekEnding}.csv`,
      ['Supply', ...data.groups.map((g) => g.name), 'Total', 'Minimum', 'Status', 'Locations below minimum'],
      data.rows.map((r) => [
        r.name, ...data.groups.map((g) => r.byGroup[String(g.groupId)] ?? 0),
        r.total, r.minimum ?? '', r.status, r.shortLocations,
      ]),
    );
  };

  const exportLocations = () => {
    if (!data) return;
    downloadCsv(
      `inventory-by-location-week-ending-${data.weekEnding}.csv`,
      ['Location', 'Region', 'Group', 'Type', 'Status', 'Counted on', 'By', ...data.rows.map((r) => r.name), 'Note'],
      data.locations.map((l) => [
        l.code, regionName(l.regionCode), groupShort(l.groupId), l.type, countStatus(l.status, data.isOpen).label,
        l.businessDate ?? '', l.by ?? '',
        // Blank, not 0, where a location was not asked or did not count.
        ...data.rows.map((r) => l.quantities[String(r.equipmentTypeId)] ?? ''),
        l.note ?? '',
      ]),
    );
  };

  // Who is missing, grouped the way the rollup's columns are, with how much of
  // each group is in — "30 of 57 not in" says more than 30 names in a row.
  const missingByGroup = data
    ? data.groups
        .map((g) => ({
          ...g,
          active: data.locations.filter((l) => l.groupId === g.groupId).length,
          // Natural order, so Route Truck 02 … 40 read in sequence.
          missing: data.notReported
            .filter((n) => n.group === g.name)
            .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' })),
        }))
        .filter((g) => g.missing.length > 0)
    : [];

  // By location is one supply at a time: every location that reported it, lowest
  // against its minimum first. The missing locations are already listed above.
  const reported = data ? data.locations.filter((l) => l.status !== 'missing') : [];
  const countedSupplies = data
    ? data.rows.filter((r) => reported.some((l) => String(r.equipmentTypeId) in l.quantities))
    : [];
  // Start on the supply with the most locations below minimum, else the first.
  const supply =
    countedSupplies.find((r) => r.equipmentTypeId === pickedSupply)
    ?? [...countedSupplies].sort((a, b) => b.shortLocations - a.shortLocations)[0];
  const key = supply ? String(supply.equipmentTypeId) : '';
  const carries = (locationId: number) => {
    const location = reference.locations.find((l) => l.locationId === locationId);
    return !!supply && !!location && !!typeOf(location.locationTypeId)?.equipmentTypeIds.includes(supply.equipmentTypeId);
  };
  const supplyRows = supply
    ? reported
        .filter((l) => key in l.quantities)
        .map((l) => ({ location: l, ready: l.quantities[key], minimum: l.minimums[key] as number | undefined }))
        .sort((a, b) => {
          const below = (x: typeof a) => (x.minimum != null && x.ready < x.minimum ? x.ready / Math.max(x.minimum, 1) : 2);
          return below(a) - below(b) || a.ready - b.ready || a.location.code.localeCompare(b.location.code, undefined, { numeric: true });
        })
    : [];
  const notCountedAt = supply ? reported.filter((l) => !(key in l.quantities) && carries(l.locationId)) : [];
  // Bars compare each location with its own minimum, so a big site never flattens
  // the rest; with no minimums set there is nothing to compare, and no bars.
  const hasMinimums = supplyRows.some((r) => r.minimum != null);
  const belowCount = supplyRows.filter((r) => r.minimum != null && r.ready < r.minimum).length;

  // Or one location at a time: every supply it counted, plus any on its list it
  // left out, in the totals table's order.
  const location = reported.find((l) => l.locationId === pickedLocation) ?? reported[0];
  const listedAt = (locationId: number) => {
    const record = reference.locations.find((l) => l.locationId === locationId);
    return (record && typeOf(record.locationTypeId)?.equipmentTypeIds) ?? [];
  };
  const locationRows = location && data
    ? data.rows
        .filter((r) => String(r.equipmentTypeId) in location.quantities || listedAt(location.locationId).includes(r.equipmentTypeId))
        .map((r) => ({
          supply: r,
          onHand: location.quantities[String(r.equipmentTypeId)] as number | undefined,
          minimum: location.minimums[String(r.equipmentTypeId)] as number | undefined,
        }))
    : [];
  const locationHasMinimums = locationRows.some((r) => r.minimum != null);
  const locationBelow = locationRows.filter((r) => r.minimum != null && (r.onHand ?? 0) < r.minimum).length;

  const totalsSort = useTableSort(data?.rows ?? [], {
    supply: (r) => r.name,
    ...Object.fromEntries((data?.groups ?? []).map((g) => [`group-${g.groupId}`, (r: RollupRow) => r.byGroup[String(g.groupId)] ?? 0])),
    total: (r) => r.total,
    minimum: (r) => r.minimum,
    status: (r) => STATUS_RANK[r.status],
  });
  const locationSort = useTableSort(locationRows, {
    supply: (x) => x.supply.name,
    onHand: (x) => x.onHand,
    minimum: (x) => x.minimum,
    against: (x) => shareOfMinimum(x.onHand, x.minimum),
  });
  const supplySort = useTableSort(supplyRows, {
    location: (x) => x.location.code,
    region: (x) => regionName(x.location.regionCode),
    date: (x) => x.location.businessDate,
    onHand: (x) => x.ready,
    minimum: (x) => x.minimum,
    against: (x) => shareOfMinimum(x.ready, x.minimum),
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">
            Week ending {formatLongDate(week)}
            {isCurrent ? ' · this week, counts due end of day Wednesday' : data && !data.isOpen ? ' · closed' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn--sm" disabled={!data} onClick={exportTotals}>Export totals</button>
          <button className="btn btn--sm" disabled={!data} onClick={exportLocations}>Export by location</button>
        </div>
      </div>

      <div className="filter-row">
        <WeekSelect value={week} onChange={setWeek} />
        <RegionSelect allowed={locationsFor('counts', 'view')} value={region} onChange={setRegion} />
      </div>

      {rollup.isError && <ErrorBanner message={(rollup.error as Error).message} />}
      {rollup.isLoading && <div className="spinner-note">Adding up the week…</div>}

      {data && (
        <>
          {/* Coverage first: a total is only as good as the locations inside it. */}
          <div className={`banner ${missing > 0 ? 'banner--conflict' : 'banner--info'}`}>
            <div>
              <strong>{data.coverage.reported} of {data.coverage.active} locations reported.</strong>{' '}
              {missing > 0
                ? `The totals below cover only those ${data.coverage.reported}; ${missing} ${missing === 1 ? 'is' : 'are'} ${data.isOpen ? 'not in yet' : 'missing'}.`
                : 'Every active location is in.'}
            </div>
          </div>

          <div className="table-wrap" style={{ marginBottom: 8 }}>
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="SUPPLY" column="supply" sort={totalsSort.sort} onSort={totalsSort.onSort} />
                  {data.groups.map((g) => (
                    <SortableHeader key={g.groupId} label={g.shortName.toUpperCase()} column={`group-${g.groupId}`}
                      sort={totalsSort.sort} onSort={totalsSort.onSort} style={{ textAlign: 'center' }} />
                  ))}
                  <SortableHeader label="TOTAL" column="total" sort={totalsSort.sort} onSort={totalsSort.onSort} className="num" />
                  <SortableHeader label="MINIMUM" column="minimum" sort={totalsSort.sort} onSort={totalsSort.onSort} style={{ textAlign: 'center' }} />
                  <SortableHeader label="STATUS" column="status" sort={totalsSort.sort} onSort={totalsSort.onSort} />
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <EmptyRow colSpan={data.groups.length + 4}>No supplies are set up yet.</EmptyRow>
                )}
                {totalsSort.rows.map((r) => (
                  <tr key={r.equipmentTypeId}>
                    <td className="strong">{r.name}</td>
                    {data.groups.map((g) => (
                      <td key={g.groupId} style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {(r.byGroup[String(g.groupId)] ?? 0).toLocaleString()}
                      </td>
                    ))}
                    <td className="num strong">{r.total.toLocaleString()}</td>
                    <td style={{ textAlign: 'center' }}>{r.minimum != null ? r.minimum.toLocaleString() : '—'}</td>
                    <td>
                      <TonePill tone={r.status === 'SHORT' ? 'urgent' : r.status === 'OK' ? 'good' : 'attention'}>
                        {r.status}
                      </TonePill>
                      {r.shortLocations > 0 && (
                        <div className="meta">{r.shortLocations} {r.shortLocations === 1 ? 'location' : 'locations'} below minimum</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="meta" style={{ marginBottom: 22 }}>
            Minimum adds up the minimum on-hand targets of the locations that reported and carry the item. SET TARGET
            means none of them has one yet.
          </div>

          {missingByGroup.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <div className="section-label" style={{ marginBottom: 0 }}>
                  {data.isOpen ? 'NOT REPORTED YET' : 'NOT REPORTED'}
                </div>
                <span className="meta">
                  {data.notReported.length} {data.notReported.length === 1 ? 'location' : 'locations'} · choose one to enter its count
                </span>
              </div>
              {/* One card, a row per group: the group and how much of it is in on
                  the left, its missing locations in even columns across the rest.
                  A group missing one location takes one short row, rather than a
                  card of its own leaving a gap beside a group missing twenty-five. */}
              <div className="card card--flush">
                {missingByGroup.map((g, i) => {
                  const inShare = g.active > 0 ? (g.active - g.missing.length) / g.active : 0;
                  return (
                    <div
                      key={g.groupId}
                      style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px 28px', padding: '14px 18px',
                        borderTop: i > 0 ? '1px solid var(--rule)' : undefined,
                      }}
                    >
                      <div style={{ flex: '0 0 220px', maxWidth: '100%' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{g.name}</div>
                        <div className="meta" style={{ marginTop: 2 }}>
                          <strong style={{ color: data.isOpen ? 'var(--text-secondary)' : 'var(--red)' }}>{g.missing.length}</strong>
                          {' '}of {g.active} not in
                        </div>
                        <div
                          className="bar-track"
                          style={{ marginTop: 8, height: 5 }}
                          title={`${g.active - g.missing.length} of ${g.active} reported`}
                        >
                          <div className="bar-fill" style={{ width: `${inShare * 100}%`, background: 'var(--green)' }} />
                        </div>
                      </div>
                      <div
                        style={{
                          flex: '1 1 320px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '6px 16px', alignContent: 'start',
                        }}
                      >
                        {g.missing.map((n) => (
                          <Link
                            key={n.locationId}
                            to={`/counts/${n.locationId}?periodEnd=${data.weekEnding}`}
                            title={`${n.type} · ${regionName(n.regionCode)} — enter its count`}
                            style={{ fontSize: 12.5, fontWeight: 600, textDecoration: 'none', lineHeight: 1.35 }}
                          >
                            {n.code}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px 14px', flexWrap: 'wrap', marginBottom: 10 }}>
              <div className="section-label" style={{ marginBottom: 0 }}>ON HAND BY</div>
              <div className="segmented" role="tablist" aria-label="Show on hand by">
                {(['supply', 'location'] as const).map((v) => (
                  <button
                    key={v}
                    role="tab"
                    aria-selected={view === v}
                    className={`segmented__item${view === v ? ' segmented__item--active' : ''}`}
                    onClick={() => setView(v)}
                  >
                    {v === 'supply' ? 'Supply' : 'Location'}
                  </button>
                ))}
              </div>
              {view === 'supply' && supply && (
                <>
                  <select
                    id="on-hand-supply"
                    className="control"
                    aria-label="Supply"
                    value={supply.equipmentTypeId}
                    onChange={(e) => setPickedSupply(Number(e.target.value))}
                  >
                    {countedSupplies.map((r) => (
                      <option key={r.equipmentTypeId} value={r.equipmentTypeId}>
                        {r.name}{r.shortLocations > 0 ? ` · ${r.shortLocations} below minimum` : ''}
                      </option>
                    ))}
                  </select>
                  <span className="meta">
                    {supplyRows.length} {supplyRows.length === 1 ? 'location' : 'locations'} counted it
                    {belowCount > 0 && <> · <span style={{ color: 'var(--red)', fontWeight: 700 }}>{belowCount} below minimum</span></>}
                  </span>
                </>
              )}
              {view === 'location' && location && (
                <>
                  <select
                    id="on-hand-location"
                    className="control"
                    aria-label="Location"
                    value={location.locationId}
                    onChange={(e) => setPickedLocation(Number(e.target.value))}
                  >
                    {data.groups.map((g) => {
                      const inGroup = reported.filter((l) => l.groupId === g.groupId);
                      return inGroup.length === 0 ? null : (
                        <optgroup key={g.groupId} label={g.name}>
                          {inGroup.map((l) => (
                            <option key={l.locationId} value={l.locationId}>
                              {l.code}{l.short.length > 0 ? ` · ${l.short.length} below minimum` : ''}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                  <span className="meta">
                    {location.type} · {regionName(location.regionCode)} · counted <CountedBy location={location} />
                  </span>
                </>
              )}
            </div>

            {view === 'location' ? (
              !location ? (
                <div className="card meta">No counts for this week yet.</div>
              ) : (
                <>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <SortableHeader label="SUPPLY" column="supply" sort={locationSort.sort} onSort={locationSort.onSort} />
                          <SortableHeader label="ON HAND" column="onHand" sort={locationSort.sort} onSort={locationSort.onSort} style={{ textAlign: 'center' }} />
                          <SortableHeader label="MINIMUM" column="minimum" sort={locationSort.sort} onSort={locationSort.onSort} style={{ textAlign: 'center' }} />
                          {locationHasMinimums && (
                            <SortableHeader label="AGAINST MINIMUM" column="against" sort={locationSort.sort} onSort={locationSort.onSort} style={{ width: '30%' }} />
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {locationSort.rows.map(({ supply: r, onHand, minimum }) => {
                          const below = minimum != null && (onHand ?? 0) < minimum;
                          return (
                            <tr key={r.equipmentTypeId}>
                              <td className="strong">{r.name}</td>
                              <td
                                style={{
                                  textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                                  color: onHand === undefined ? 'var(--muted)' : below ? 'var(--red)' : 'var(--text)',
                                }}
                              >
                                {onHand === undefined ? <span className="meta">not counted</span> : onHand.toLocaleString()}
                              </td>
                              <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                                {minimum != null ? minimum.toLocaleString() : '—'}
                              </td>
                              {locationHasMinimums && (
                                <td><AgainstMinimum onHand={onHand ?? 0} minimum={minimum} /></td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="meta" style={{ marginTop: 8 }}>
                    {locationBelow > 0 && (
                      <><span style={{ color: 'var(--red)', fontWeight: 700 }}>{locationBelow} below minimum.</span>{' '}</>
                    )}
                    {!locationHasMinimums && <>No minimums are set for {location.code} yet. </>}
                    {location.note && <>Note: “{location.note}” </>}
                    <Link to={`/counts/${location.locationId}?periodEnd=${data.weekEnding}`} style={{ fontWeight: 600, textDecoration: 'none' }}>
                      Open {location.code}'s count →
                    </Link>
                  </div>
                </>
              )
            ) : !supply ? (
              <div className="card meta">No counts for this week yet.</div>
            ) : (
              <>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <SortableHeader label="LOCATION" column="location" sort={supplySort.sort} onSort={supplySort.onSort} />
                        <SortableHeader label="REGION" column="region" sort={supplySort.sort} onSort={supplySort.onSort} />
                        <SortableHeader label="COUNT DATE" column="date" sort={supplySort.sort} onSort={supplySort.onSort} />
                        <SortableHeader label="ON HAND" column="onHand" sort={supplySort.sort} onSort={supplySort.onSort} style={{ textAlign: 'center' }} />
                        <SortableHeader label="MINIMUM" column="minimum" sort={supplySort.sort} onSort={supplySort.onSort} style={{ textAlign: 'center' }} />
                        {hasMinimums && (
                          <SortableHeader label="AGAINST MINIMUM" column="against" sort={supplySort.sort} onSort={supplySort.onSort} style={{ width: '30%' }} />
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {supplySort.rows.map(({ location: l, ready, minimum }) => {
                        const below = minimum != null && ready < minimum;
                        return (
                          <tr key={l.locationId}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <Link
                                to={`/counts/${l.locationId}?periodEnd=${data.weekEnding}`}
                                style={{ fontWeight: 700, textDecoration: 'none' }}
                              >
                                {l.code}
                              </Link>
                              <div className="meta">{l.type}</div>
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>{regionName(l.regionCode)}</td>
                            <td style={{ whiteSpace: 'nowrap' }} title={l.note ?? undefined}>
                              <CountedBy location={l} />
                            </td>
                            <td
                              style={{
                                textAlign: 'center', fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                                color: below ? 'var(--red)' : 'var(--text)',
                              }}
                            >
                              {ready.toLocaleString()}
                            </td>
                            <td style={{ textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                              {minimum != null ? minimum.toLocaleString() : '—'}
                            </td>
                            {hasMinimums && (
                              <td><AgainstMinimum onHand={ready} minimum={minimum} /></td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="meta" style={{ marginTop: 8 }}>
                  {hasMinimums
                    ? "Each bar is that location's count against its own minimum; a full green bar is at or above it."
                    : <>No minimums are set for {supply.name} yet. Set them under <Link to="/setup/targets">Targets</Link> to see who is running low.</>}
                  {notCountedAt.length > 0 && (
                    <> Reported but didn't count {supply.name}, though it's on their list:{' '}
                      {notCountedAt.map((l, i) => (
                        <span key={l.locationId}>
                          {i > 0 && ', '}
                          <Link to={`/counts/${l.locationId}?periodEnd=${data.weekEnding}`} style={{ fontWeight: 600, textDecoration: 'none' }}>
                            {l.code}
                          </Link>
                        </span>
                      ))}.
                    </>
                  )}
                </div>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** When a location's count was taken, and who entered it. */
function CountedBy({ location: l }: { location: RollupLocation }) {
  return (
    <>
      {l.businessDate ? formatDate(l.businessDate) : '—'}
      <span className="meta">
        {/* An imported count has no person to name, so it shows only its date. */}
        {l.status !== 'imported' && l.by && ` · ${l.by}`}
        {l.status === 'late' && <span style={{ color: 'var(--orange)', fontWeight: 700 }}> · late</span>}
        {l.note && ' · note'}
      </span>
    </>
  );
}

/** A count against its own minimum: a bar that fills at the minimum, and how many short. */
function AgainstMinimum({ onHand, minimum }: { onHand: number; minimum?: number }) {
  if (minimum == null) return <span className="meta">No minimum</span>;
  const below = onHand < minimum;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div className="bar-track" style={{ height: 7 }}>
        <div
          className="bar-fill"
          style={{
            width: `${Math.min(onHand / Math.max(minimum, 1), 1) * 100}%`,
            background: below ? 'var(--red)' : 'var(--green)',
          }}
        />
      </div>
      <span
        style={{
          width: 64, fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          color: below ? 'var(--red)' : 'var(--green-deep)',
        }}
      >
        {below ? `${(minimum - onHand).toLocaleString()} short` : 'OK'}
      </span>
    </div>
  );
}
